import { useCallback, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Settings } from 'lucide-react';
import { HelpPopover } from '@/components/ui/HelpPopover';
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions';
import { markUiSettingApplied, saveUiSetting } from '@/lib/ui-settings';

/**
 * Tabellengerüst des Listen-Standards, einmal für alle drei
 * Lohnverrechnungs-Seiten (AP-22-B).
 *
 * Es setzt dasselbe um wie die Referenz `SummaryPage` — konfigurierbare,
 * sortier-, verschieb- und breitenverstellbare Spalten, Zahnrad-Modus,
 * Mandanten-Standard, Summenzeile, Zebra-Streifen. Ausgelagert, weil dieses
 * Modul drei Listen gleichzeitig bekommt: Drei Kopien desselben Gerüsts würden
 * beim ersten Nachziehen am Standard auseinanderlaufen, und der Unterschied
 * fiele niemandem auf.
 *
 * Die **fachlichen** Teile bleiben bei den Seiten: Filterfelder, Spaltensatz,
 * Zellinhalte, Sortierregeln. Nur die Mechanik steht hier.
 */

export interface ListColumn {
  key: string;
  label: string;
  align?: 'right';
  sortable?: boolean;
}

export interface ListColumnState {
  columnKeys: string[];
  columnWidths: Record<string, number>;
  sortKey: string;
  sortDir: 'asc' | 'desc';
}

interface Props<T> {
  /** Alle wählbaren Spalten (ohne die fixe erste). */
  allColumns: ListColumn[];
  /** Die fixe erste Spalte — sie lässt sich sortieren, aber nicht entfernen. */
  fixedColumn: ListColumn;
  state: ListColumnState;
  onStateChange: (next: ListColumnState) => void;
  /** Schlüssel der gespeicherten Einstellung (`columnbar_layout_…`). */
  settingKey: string;
  defaultState: ListColumnState;

  rows: T[];
  rowKey: (row: T) => string;
  /** Inhalt der fixen ersten Zelle. */
  renderFixedCell: (row: T) => React.ReactNode;
  renderCell: (row: T, col: ListColumn) => React.ReactNode;

  loading?: boolean;
  error?: string | null;
  emptyText: string;
  /** Text in der ersten Zelle der Summenzeile, z. B. „13 Feiertage". */
  summaryText: string;
}

export function ListTable<T>({
  allColumns,
  fixedColumn,
  state,
  onStateChange,
  settingKey,
  defaultState,
  rows,
  rowKey,
  renderFixedCell,
  renderCell,
  loading = false,
  error = null,
  emptyText,
  summaryText,
}: Props<T>) {
  const [configMode, setConfigMode] = useState(false);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragKeyRef = useRef<string | null>(null);
  const resizeRef = useRef<{ key: string; startX: number; startW: number } | null>(null);

  const update = useCallback(
    (updater: (prev: ListColumnState) => ListColumnState) => {
      const next = updater(state);
      saveUiSetting(settingKey, next);
      onStateChange(next);
    },
    [state, onStateChange, settingKey],
  );

  const handleSort = (col: ListColumn) => {
    if (!col.sortable) return;
    update((prev) => ({
      ...prev,
      sortKey: col.key,
      sortDir: prev.sortKey === col.key && prev.sortDir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleDrop = (targetKey: string) => {
    const srcKey = dragKeyRef.current;
    if (!srcKey || srcKey === targetKey) {
      setDragOverKey(null);
      return;
    }
    update((prev) => {
      const keys = [...prev.columnKeys];
      const from = keys.indexOf(srcKey);
      const to = keys.indexOf(targetKey);
      if (from < 0 || to < 0) return prev;
      keys.splice(from, 1);
      keys.splice(to, 0, srcKey);
      return { ...prev, columnKeys: keys };
    });
    setDragOverKey(null);
  };

  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    const th = (e.target as HTMLElement).closest('th');
    resizeRef.current = { key, startX: e.clientX, startW: th?.getBoundingClientRect().width ?? 120 };
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const newW = Math.max(60, resizeRef.current.startW + (ev.clientX - resizeRef.current.startX));
      update((prev) => ({
        ...prev,
        columnWidths: { ...prev.columnWidths, [resizeRef.current!.key]: newW },
      }));
    };
    const onUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const shown = state.columnKeys
    .map((k) => allColumns.find((c) => c.key === k))
    .filter((c): c is ListColumn => c !== undefined);
  const availableToAdd = allColumns.filter((c) => !state.columnKeys.includes(c.key));
  const totalCols = 1 + shown.length + (configMode && availableToAdd.length > 0 ? 1 : 0) + 1;

  const sortIcon = (key: string) =>
    state.sortKey === key ? (
      state.sortDir === 'asc' ? (
        <ArrowUp className="h-3 w-3 text-primary" />
      ) : (
        <ArrowDown className="h-3 w-3 text-primary" />
      )
    ) : (
      <ArrowUpDown className="h-3 w-3 opacity-30" />
    );

  return (
    <div className="relative overflow-hidden rounded-lg border border-border bg-card shadow-sm">
      <button
        type="button"
        onClick={() => setConfigMode((v) => !v)}
        className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
          configMode
            ? 'bg-primary text-primary-foreground'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
        title="Spalten konfigurieren"
      >
        {configMode ? <Check size={14} /> : <Settings size={14} />}
      </button>
      <HelpPopover topic="columns" className="absolute right-8 top-1.5" />

      {configMode && (
        <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
          <LayoutDefaultActions
            settingKey={settingKey}
            currentValue={state}
            onReset={(value) => {
              const next = (value as ListColumnState | null) ?? defaultState;
              markUiSettingApplied(settingKey, next);
              onStateChange(next);
            }}
          />
        </div>
      )}

      {error && (
        <div className="m-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50">
            <tr>
              <th
                className="relative px-4 py-1.5 text-left font-medium text-foreground"
                style={
                  state.columnWidths[fixedColumn.key]
                    ? { width: state.columnWidths[fixedColumn.key] }
                    : undefined
                }
              >
                {fixedColumn.sortable ? (
                  <button
                    type="button"
                    onClick={() => handleSort(fixedColumn)}
                    className="flex items-center gap-1 hover:text-primary"
                  >
                    {fixedColumn.label}
                    {sortIcon(fixedColumn.key)}
                  </button>
                ) : (
                  <span>{fixedColumn.label}</span>
                )}
                <div
                  className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                  onMouseDown={(e) => handleResizeStart(e, fixedColumn.key)}
                />
              </th>

              {shown.map((col) => (
                <th
                  key={col.key}
                  className={`relative select-none px-4 py-1.5 font-medium text-foreground
                    ${col.align === 'right' ? 'text-right' : 'text-left'}
                    ${configMode ? 'cursor-grab' : ''}
                    ${dragOverKey === col.key ? 'bg-primary/10 outline outline-2 outline-primary' : ''}`}
                  draggable={configMode}
                  onDragStart={configMode ? () => (dragKeyRef.current = col.key) : undefined}
                  onDragOver={
                    configMode
                      ? (e) => {
                          e.preventDefault();
                          setDragOverKey(col.key);
                        }
                      : undefined
                  }
                  onDrop={configMode ? () => handleDrop(col.key) : undefined}
                  onDragEnd={
                    configMode
                      ? () => {
                          dragKeyRef.current = null;
                          setDragOverKey(null);
                        }
                      : undefined
                  }
                  style={
                    state.columnWidths[col.key] ? { width: state.columnWidths[col.key] } : undefined
                  }
                >
                  <div
                    className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(col)}
                        className="flex items-center gap-1 hover:text-primary"
                      >
                        {col.label}
                        {sortIcon(col.key)}
                      </button>
                    ) : (
                      <span>{col.label}</span>
                    )}
                    {configMode && (
                      <button
                        type="button"
                        onClick={() =>
                          update((prev) => ({
                            ...prev,
                            columnKeys: prev.columnKeys.filter((k) => k !== col.key),
                          }))
                        }
                        className="ml-1 text-muted-foreground hover:text-destructive"
                        title="Spalte entfernen"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <div
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                    onMouseDown={(e) => handleResizeStart(e, col.key)}
                  />
                </th>
              ))}

              {configMode && availableToAdd.length > 0 && (
                <th className="px-2 py-1.5">
                  <select
                    className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
                    defaultValue=""
                    onChange={(e) => {
                      const key = e.target.value;
                      if (!key) return;
                      update((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }));
                      e.target.value = '';
                    }}
                  >
                    <option value="">+ Spalte</option>
                    {availableToAdd.map((c) => (
                      <option key={c.key} value={c.key}>
                        {c.label || c.key}
                      </option>
                    ))}
                  </select>
                </th>
              )}
              <th className="w-4" />
            </tr>
          </thead>

          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={totalCols}
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                >
                  Laden…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td
                  colSpan={totalCols}
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                >
                  {emptyText}
                </td>
              </tr>
            )}

            {rows.map((row, idx) => (
              <tr
                key={rowKey(row)}
                className={`border-b border-border last:border-0 ${
                  idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                } transition-colors hover:bg-accent/30`}
              >
                <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                  {renderFixedCell(row)}
                </td>
                {shown.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-1.5 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}
                  >
                    {renderCell(row, col)}
                  </td>
                ))}
                <td />
              </tr>
            ))}

            {rows.length > 0 && (
              <tr className="border-t-2 border-border bg-muted/50 font-medium">
                <td className="whitespace-nowrap px-4 py-1.5 text-muted-foreground">
                  {loading ? 'Laden…' : summaryText}
                </td>
                {shown.map((col) => (
                  <td key={col.key} className="px-4 py-1.5" />
                ))}
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
